import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { OrientationGuard } from './OrientationGuard';
import { useAuthStore } from '../store/auth.store.js';

interface RootLayoutProps {
  children: ReactNode;
}

/**
 * Root layout wrapping the entire app.
 * Handles orientation detection, fullscreen enforcement, and session checking.
 */
export function RootLayout({ children }: RootLayoutProps) {
  const { checkSession } = useAuthStore();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const location = useLocation();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/';

  // iOS Safari on iPhones does not support standard HTML5 Fullscreen API.
  // We check compatibility to avoid blocking iOS users.
  const isFullscreenSupported = 
    typeof document.documentElement.requestFullscreen === 'function' ||
    typeof (document.documentElement as any).webkitRequestFullscreen === 'function' ||
    typeof (document.documentElement as any).msRequestFullscreen === 'function';

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (!isFullscreenSupported) return;

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    // Check initial state
    setIsFullscreen(!!document.fullscreenElement);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isFullscreenSupported]);

  const enterFullscreen = async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if ((el as any).webkitRequestFullscreen) {
        await (el as any).webkitRequestFullscreen();
      } else if ((el as any).msRequestFullscreen) {
        await (el as any).msRequestFullscreen();
      }
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
    }
  };

  return (
    <div className="relative min-h-screen min-h-[100dvh] overflow-hidden bg-game-bg">
      <OrientationGuard />
      
      {isFullscreenSupported && !isFullscreen && !isLoginPage ? (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-slate-950 px-6 text-center select-none">
          <div className="absolute w-72 h-72 rounded-full bg-purple-600/10 blur-3xl -z-10" />
          
          <div className="glass max-w-sm w-full p-8 border border-white/10 rounded-3xl shadow-glow-lg text-center space-y-6">
            <div className="text-4xl animate-bounce">🖥️</div>
            <div className="space-y-2">
              <h2 className="font-display text-xl font-black text-white uppercase tracking-wider">
                Fullscreen Required
              </h2>
              <p className="text-xs text-surface-400 font-semibold leading-relaxed">
                Tambola Online requires fullscreen mode for the best multiplayer gaming experience.
              </p>
            </div>
            <button
              onClick={enterFullscreen}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-xs font-black uppercase tracking-widest text-white cursor-pointer shadow-glow-accent transition-all hover:scale-105"
            >
              Enter Fullscreen
            </button>
          </div>
        </div>
      ) : (
        children
      )}
    </div>
  );
}
